import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    AppState,
    ActionStep,
    ActionStepTemplate,
    GoalActions,
    Goals,
    NewGoalActions
} from 'types';

import ActionStepInput from './ActionStepInput';
import ActionStepSuggestionList from './ActionStepSuggestionList';
import SuggestionSectionHeader from './SuggestionSectionHeader';
import SuggestionSectionFooter from './SuggestionSectionFooter';
import TemplateCard from './TemplateCard';
import Card from 'components/_shared/Card';
import LoadingCircle from 'components/_shared/LoadingCircle';
import Overlay from 'components/_shared/Overlay';
import { DoneButton } from 'components/_shared/buttons';

import { newGoalThunk } from 'redux/actions/goals';

import { fetchMemberActionStepHistorySuggestions } from 'api/goal_actions';

import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import { makeStyles } from '@mui/styles';
const HISTORY_PER_PAGE = 2;
const useStyles = makeStyles(() => ({
    cancelFormButton: {
        background: 'transparent',
        borderRadius: '100px',
        color: '#F5577A',
        fontSize: '1.25rem',
        fontWeight: 700,
        padding: '15px 30px',
        textTransform: 'none'
    },
    cancelBtn: {
        fontFamily: 'Manrope',
        fontWeight: '700',
        fontSize: '1.25rem',
        color: '#8185D6',
        paddingTop: '15px',
        background: 'transparent',
        margin: '0'
    },
    cardTitle: {
        position: 'absolute',
        top: '-1rem',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#8882D8',
        borderRadius: '100px',
        color: '#fff',
        fontWeight: '800',
        letterSpacing: '0.1em',
        lineHeight: '100%',
        padding: '8px 20px',
        width: 'fit-content',
        textTransform: 'uppercase'
    },
    fadeContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        margin: '20px 0',
        width: '100%'
    },
    inputFields: {
        height: '3.125rem',
        width: '100%',
        borderRadius: '40px',
        fontFamily: 'Manrope',
        fontSize: '1.75rem',
        fontWeight: '600',
        color: '#1f1f51',
        letterSpacing: '-0.21px',
        background: 'transparent',
        paddingLeft: '15px',
        resize: 'none',
        '&::placeholder': {
            textAlign: 'center',
            color: '#A8A8BF',
            fontSize: '1.25rem'
        },
        '&:focus::placeholder': {
            textAlign: 'left',
            fontStyle: 'italic',
            fontWeight: '500'
        },
        '&:focus': {
            outline: 'none',
            border: '0 !important',
            textAlign: 'left',
            padding: '4px 8px',
            textDecoration: 'underline',
            textDecorationColor: '#E3E4FA',
            textDecorationThickness: '4px'
        }
    },
    needWrapper: {
        position: 'relative',
        borderRadius: '20px',
        padding: '20px',
        margin: '20px 0 80px 0',
        '& button': {
            cursor: 'pointer'
        },
        boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.10)'
    },
    submitBtn: {
        fontFamily: 'Manrope',
        fontWeight: '800',
        fontSize: '1.25rem',
        color: '#fff',
        width: '50%',
        background: 'linear-gradient(90deg, #9897E3 0%, #B8C2FF 100%)',
        padding: '15px 0',
        margin: '0'
    },
    submitBtnContainer: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px'
    },
    errorText: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: '#F5577A',
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: '12px',
        textAlign: 'center' as const
    },
    writeBtn: {
        fontFamily: 'Manrope',
        fontWeight: '800',
        fontSize: '16px',
        padding: '15px 20px',
        margin: 'auto'
    },
    suggestionSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },

    initialOptionsContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        backgroundColor: '#FBFBFF',
        borderRadius: '20px',
        boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.10)',
        padding: '24px 24px 32px 24px'
    },
    '@global': {
        '#action-template-card, #need-div': {
            margin: '0 !important'
        }
    }
}));

type ActionStepFormProps = {
    actionSuggestions: any;
    addFailed?: boolean;
    editAction?: number[];
    editForm?: {
        actionIndex?: number;
        costInputState: number;
        customGoalInputState?: string;
        dateInputState: number | string;
        descriptionInput: boolean;
        descriptionInputState: string;
        goalInputState?: Goals[];
        qolMeasureState?: number;
    };
    failedAction?: NewGoalActions | null;
    from: string;
    handleCancelClick?: () => void;
    holderID?: number;
    intakeGoals?: any[];
    onClick?: () => void;
    ref?: any;
    remainingBonusAmount: number;
    bonusType?: 'base' | 'extra';
    isExtraBonusParticipant?: boolean;
    samaritanYearlyDepleted?: boolean;
    yearlyFundsDepleted?: boolean;
    setEditAction?: (value: number[]) => void;
    setEditNeed?: (value: number[]) => void;
    setFormComplete?: (value: boolean) => void;
    setGuide?: (value: boolean) => void;
    setIntakeNeeds?: (value: any[]) => void;
    setIntakeGoals?: (value: any[]) => void;
    setShowEdit?: (value: boolean) => void;
    setShowForm: (value: boolean) => void;
    setTemporaryAction?: (
        temporaryAction: ActionStep | ActionStepTemplate | null
    ) => void;
    errorMessage?: string;
    showCancel?: boolean;
    showEdit?: boolean;
    templateActions?: ActionStepTemplate[] | null;
    temporaryAction?: ActionStep | ActionStepTemplate | null;
};

const calculateDate = (due_at: number | string) => {
    if (typeof due_at === 'string' && due_at <= new Date().toISOString()) {
        return new Date(`${due_at}T00:00:00`).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    const parseDateString = (dateStr: string): Date | null => {
        let date;

        if (dateStr.includes('T')) {
            date = new Date(dateStr);
        } else {
            date = new Date(dateStr + 'T00:00:00');
        }

        return isNaN(date.getTime()) ? null : date;
    };

    const calculateDaysDifference = (date1: Date, date2: Date): number => {
        const diffTime = Math.abs(date2.getTime() - date1.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    if (typeof due_at === 'string') {
        const dueDate = parseDateString(due_at);
        if (!dueDate) return 'Invalid date format';

        const today = new Date();
        const diffDays = calculateDaysDifference(today, dueDate);

        if (diffDays <= 3) {
            return 'ASAP';
        } else if (diffDays === 30) {
            return '1 month';
        } else {
            return `${diffDays} Days`;
        }
    } else if (typeof due_at === 'number') {
        if (due_at === 0 || !due_at) {
            return 'No due date';
        } else if (due_at === 30) {
            return '1 month';
        } else {
            return `${due_at} days`;
        }
    } else {
        return 'Invalid input';
    }
};

const calculateDateDifference = (date1: string, date2: string) => {
    const today = new Date();
    const date1Obj = new Date(date1);
    const date2Obj = new Date(date2);
    const timeDiff = Math.abs(date2Obj.getTime() - date1Obj.getTime());
    const newDate = new Date(timeDiff + today.getTime());

    return newDate.toISOString();
};

const ActionStepForm: React.FC<ActionStepFormProps> = forwardRef(
    (
        {
            actionSuggestions,
            addFailed,
            editAction,
            editForm,
            failedAction,
            from,
            handleCancelClick,
            holderID,
            intakeGoals,
            remainingBonusAmount,
            bonusType = 'base',
            isExtraBonusParticipant = false,
            samaritanYearlyDepleted = false,
            yearlyFundsDepleted = false,
            errorMessage,
            showCancel,
            showEdit,
            setEditAction,
            setFormComplete,
            setGuide,
            setIntakeGoals,
            setShowForm,
            setTemporaryAction,
            templateActions,
            temporaryAction
        },
        ref
    ) => {
        const classes = useStyles();
        const dispatch = useDispatch();
        const user = useSelector((state: AppState) => state.user.user);

        const [cost, setCost] = useState<null | number>(null);
        const [costInput, setCostInput] = useState(false);
        const [customDescription, setCustomDescription] = useState(``);
        const [date, setDate] = useState<any>(``);
        const [dateInput, setDateInput] = useState(false);
        const [dateSuggestion, setDateSuggestion] = useState(``);
        const [description, setDescription] = useState(``);
        const [descriptionInput, setDescriptionInput] = useState(false);
        const [displayCostBtns, setDisplayCostBtns] = useState(true);
        const [displayDateBtns, setDisplayDateBtns] = useState(true);
        const [displayDescriptionBtns, setDisplayDescriptionBtns] =
            useState(true);

        const [displayCustomDateInput, setDisplayCustomDateInput] =
            useState(false);
        const [displayEditCostBtns, setDisplayEditCostBtns] = useState(false);
        const [displayEditDateBtns, setDisplayEditDateBtns] = useState(false);
        const [displayEditDescriptionBtns, setDisplayEditDescriptionBtns] =
            useState(false);

        const [history, setHistory] = useState<GoalActions[] | null>(null);
        const [historyPage, setHistoryPage] = useState(1);
        const [historyHasMore, setHistoryHasMore] = useState(false);
        const [isEditing, setIsEditing] = useState({
            cost: false,
            due_dates: false,
            descriptions: false
        });
        const [isFetchingHistory, setIsFetchingHistory] = useState(false);
        const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
        const [overlay, setOverlay] = useState(false);
        const [qolMeasure, setQolMeasure] = useState<null | number>(null);
        const [useCustom, setUseCustom] = useState(
            templateActions?.length ? false : true
        );
        const [useHistory, setUseHistory] = useState(false);
        const [useTemplate, setUseTemplate] = useState(false);

        // Admin override state
        const [adminBonusOverride, setAdminBonusOverride] = useState(false);
        const isAdmin = user?.is_admin || false; // Use user.is_admin from Redux store with fallback

        // Custom action step state
        const [activeCustom, setActiveCustom] = useState(false);

        const hasTemplateActions =
            templateActions && templateActions.length > 0;

        // Custom template for "Write Your Own"
        const customTemplate = {
            action_type: 'one_time',
            bonus: 0,
            description: 'Write your own...',
            due_at: 0,
            quality_of_life_measure_id: null
        };

        // Normalize GoalActions to ActionStepTemplate format
        const normalizeGoalActionToTemplate = (item: GoalActions): ActionStepTemplate => {
            // Calculate due_at using the same method as handleUseHistory
            let normalizedDueAt = 0;
            if (item.due_at && item.created_at) {
                const dateDifference = calculateDateDifference(item.created_at, item.due_at);
                const dateDifferenceEndOfDay = new Date(dateDifference);
                
                // Convert back to days for the template format
                const today = new Date();
                const diffTime = Math.abs(dateDifferenceEndOfDay.getTime() - today.getTime());
                normalizedDueAt = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            } else if (typeof item.due_at === 'number') {
                normalizedDueAt = item.due_at;
            }

            return {
                action_type: 'one_time',
                bonus: item.match_value || 0,
                description: item.description,
                due_at: normalizedDueAt,
                quality_of_life_measure_id: item.quality_of_life_measures?.[0] || null
            };
        };

        const removeEditActionType = useCallback(
            (actionTypeIndex: number) => {
                setEditAction!(
                    editAction!.filter((action) => action !== actionTypeIndex)
                );
            },
            [editAction, setEditAction]
        );

        const handleCardClick = (e: {
            target: { tagName: string; id: string };
        }) => {
            if (
                e.target.tagName !== `INPUT` &&
                e.target.tagName !== `BUTTON` &&
                e.target.tagName !== `TEXTAREA` &&
                e.target.id !== 'edit-button'
            ) {
                hideBtns();

                setIsEditing({
                    cost: false,
                    due_dates: false,
                    descriptions: false
                });
            }

            const needDiv = document.getElementById(`need-div`);

            if (needDiv && from === 'intake') {
                needDiv.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        };

        // Custom action step handlers
        const handleCustomActionClick = (_bonus: number, _due_at: number, description: string) => {
            // Only proceed if user has actually entered a custom description
            if (description && description !== customTemplate.description && description.trim().length > 0) {
                setDescription(description);
                setDescriptionInput(true);
                setDisplayDescriptionBtns(false);
                setUseCustom(true);
                setUseTemplate(false);
                setUseHistory(false);
            }
            // If no description yet, just activate the input for editing
            setActiveCustom(true);
        };

        // Template selection handler
        const handleTemplateSelection = (item: ActionStepTemplate, _index: number) => {
            const template = item;
            let dueDate = '';

            if (template.due_at && template.due_at > 0) {
                const today = new Date();
                today.setDate(today.getDate() + template.due_at);
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                dueDate = `${today.getFullYear()}-${month}-${day}`;
                setDate(dueDate);
                setDateInput(true);
                setDisplayDateBtns(false);
                setDateSuggestion(template.due_at === 30 ? '1 month' : `${template.due_at} days`);
            }

            if (template.bonus && template.bonus > 0) {
                setCost(Math.min(template.bonus, remainingBonusAmount));
                setCostInput(true);
                setDisplayCostBtns(false);
            }

            if (template.description && template.description.length) {
                setDescription(template.description);
                setDescriptionInput(true);
                setDisplayDescriptionBtns(false);
            }

            if (template.quality_of_life_measure_id) {
                setQolMeasure(template.quality_of_life_measure_id);
            }

            setUseTemplate(true);
            setUseCustom(false);
            setUseHistory(false);

            if (setTemporaryAction) {
                const bonus =
                    remainingBonusAmount === 0
                        ? 0
                        : Math.min(template.bonus, remainingBonusAmount);
                const adjustedTemplate: ActionStep = {
                    amount: bonus,
                    bonus,
                    description: template.description,
                    due_at: dueDate || template.due_at,
                    quality_of_life_measure_id:
                        template.quality_of_life_measure_id ?? undefined
                };
                setTemporaryAction(adjustedTemplate);
            }
        };

        // History selection handler - for normalized history items
        const handleHistorySelection = (_item: ActionStepTemplate, index: number) => {
            // Find the original GoalActions item from history array
            const originalHistoryItem = history?.[index];
            if (originalHistoryItem) {
                handleUseHistory(originalHistoryItem);
            }
        };

        // Handle loading more history items
        const handleLoadMoreHistory = useCallback(async () => {
            if (!holderID || isLoadingMoreHistory || !historyHasMore) return;

            setIsLoadingMoreHistory(true);
            try {
                const response = await fetchMemberActionStepHistorySuggestions(holderID, historyPage + 1, HISTORY_PER_PAGE);
                const responseJson = await response.json();
                const newHistoryItems = responseJson.goal_actions || [];
                
                setHistory(prevHistory => [...(prevHistory || []), ...newHistoryItems]);
                setHistoryPage(prev => prev + 1);
                
                // Check if there are more items (if we got less than per_page, no more items)
                setHistoryHasMore(newHistoryItems.length === HISTORY_PER_PAGE);
            } catch (error) {
                console.error('Error loading more history:', error);
            } finally {
                setIsLoadingMoreHistory(false);
            }
        }, [holderID, historyPage, isLoadingMoreHistory, historyHasMore]);

        const handleAddForIntake = useCallback(() => {
            let adjustedDate = date;

            if (typeof date === 'number') {
                const today = new Date();
                today.setDate(today.getDate() + date);
                adjustedDate = today.toISOString().split('T')[0];
            }

            const asFormData = {
                amount: Number(cost),
                description,
                due_at: adjustedDate + 'T12:00:00-07:00',
                quality_of_life_measure_id: qolMeasure,
                ...(bonusType === 'extra' && {
                    classification: 'capital_foundation_pool' as const
                })
            };

            setOverlay(true);

            const cardTimer = setTimeout(
                () => {
                    if (!temporaryAction && setTemporaryAction) {
                        setTemporaryAction(asFormData as ActionStep);
                    }
                },
                useCustom ? 1500 : 1000
            );

            const cleanupTimer = setTimeout(
                () => {
                    clearTimeout(cardTimer);

                    if (showEdit) {
                        const updatedIntakeGoals = [...intakeGoals!];
                        const existing =
                            updatedIntakeGoals[editForm!.actionIndex!] || {};
                        const classification =
                            bonusType === 'extra'
                                ? 'capital_foundation_pool'
                                : existing.classification ===
                                    'capital_foundation_pool'
                                  ? 'capital_foundation_pool'
                                  : existing._serverData?.classification ===
                                      'capital_foundation_pool'
                                    ? 'capital_foundation_pool'
                                    : existing.classification;

                        updatedIntakeGoals[editForm!.actionIndex!] = {
                            ...existing,
                            amount: cost,
                            description: description,
                            due_at: adjustedDate,
                            quality_of_life_measure_id: qolMeasure,
                            ...(classification
                                ? { classification }
                                : {})
                        };

                        setIntakeGoals!(updatedIntakeGoals);
                        removeEditActionType(editForm!.actionIndex!);
                    }
                },
                useCustom ? 2500 : 0
            );

            return () => {
                clearTimeout(cardTimer);
                clearTimeout(cleanupTimer);
            };
        }, [
            bonusType,
            cost,
            date,
            description,
            editForm,
            intakeGoals,
            qolMeasure,
            removeEditActionType,
            setIntakeGoals,
            setTemporaryAction,
            showEdit,
            temporaryAction,
            useCustom
        ]);

        const handleAddForProfile = useCallback(() => {
            setOverlay(useCustom);
            setTimeout(() => {
                if (holderID && (cost || cost === 0)) {
                    dispatch(
                        newGoalThunk(
                            holderID!,
                            description,
                            date,
                            cost,
                            null,
                            qolMeasure,
                            adminBonusOverride && !samaritanYearlyDepleted
                                ? 'gap_in_care'
                                : bonusType === 'extra'
                                  ? 'capital_foundation_pool'
                                  : undefined
                        )
                    );
                }
                setShowForm(false);
            }, useCustom ? 2000 : 0);
        }, [
            cost,
            date,
            description,
            dispatch,
            holderID,
            qolMeasure,
            setShowForm,
            adminBonusOverride,
            bonusType,
            samaritanYearlyDepleted,
            useCustom
        ]);

        const handleUseHistory = (data: GoalActions) => {
            const { created_at, description, due_at, match_value } = data;
            const bonusAmount =
                match_value > remainingBonusAmount
                    ? remainingBonusAmount
                    : match_value;
            const dateDifference = calculateDateDifference(created_at, due_at);
            const dateDifferenceEndOfDay = new Date(dateDifference);
            dateDifferenceEndOfDay.setHours(23, 59, 59, 999);

            setUseHistory(true);

            setDescription(description);
            setDescriptionInput(true);
            setDisplayDescriptionBtns(false);

            setCost(bonusAmount);
            setCostInput(true);
            setDisplayCostBtns(false);

            if (from === 'profile') {
                setDate(dateDifferenceEndOfDay);
                setDateSuggestion(calculateDate(dateDifference));
            } else {
                const dateMinusOneDay = new Date(dateDifference);
                const dateString = dateMinusOneDay.toISOString();

                setDate(dateString.split('T')[0]);
                setDateSuggestion(calculateDate(dateString));
            }

            setDisplayDateBtns(false);
            setDateInput(true);
        };

        const hideBtns = () => {
            const conditions = [
                {
                    display: displayCostBtns,
                    condition: typeof cost === 'string' && cost > 0,
                    setter: setDisplayCostBtns
                },
                {
                    display: displayDateBtns,
                    condition: date.length,
                    setter: setDisplayDateBtns
                },
                {
                    display: displayDescriptionBtns,
                    condition: description.length,
                    setter: setDisplayDescriptionBtns
                },
                {
                    display: displayEditCostBtns,
                    condition: typeof cost === 'string' && cost > 0,
                    setter: setDisplayEditCostBtns
                },
                {
                    display: displayEditDateBtns,
                    condition: date.length,
                    setter: setDisplayEditDateBtns
                },
                {
                    display: displayEditDescriptionBtns,
                    condition: true,
                    setter: setDisplayEditDescriptionBtns
                }
            ];

            conditions.forEach(({ display, condition, setter }) => {
                if (display && condition) {
                    setter(false);
                }
            });
        };

        const isValidForm = useCallback(
            () =>
                cost !== undefined &&
                cost !== null &&
                date &&
                description &&
                (useTemplate || useHistory) &&
                from === 'intake',
            [cost, date, description, useHistory, useTemplate, from]
        );

        useEffect(() => {
            if (showEdit) {
                let adjustedEditDate = editForm!.dateInputState;
                let adjustedEditCost = editForm!.costInputState;

                if (!editForm!.costInputState) {
                    adjustedEditCost = 0;
                }

                if (typeof editForm!.dateInputState === 'number') {
                    const today = new Date();
                    today.setDate(today.getDate() + editForm!.dateInputState);
                    adjustedEditDate = today.toISOString().split('T')[0];
                }

                setCost(adjustedEditCost);
                setDate(adjustedEditDate);
                setDateSuggestion(calculateDate(editForm!.dateInputState));
                setDescription(editForm!.descriptionInputState);

                if (editForm!.qolMeasureState) {
                    setQolMeasure(editForm!.qolMeasureState);
                }
            }
        }, [showEdit, editForm]);

        useEffect(() => {
            if (isValidForm()) {
                handleAddForIntake();
            }
        }, [isValidForm, handleAddForIntake]);

        useEffect(() => {
            if (
                (cost || cost === 0) &&
                date &&
                description &&
                from === 'profile' &&
                setFormComplete
            ) {
                setFormComplete(true);
            }
        }, [cost, date, description, from, setFormComplete]);

        useEffect(() => {
            if (addFailed && failedAction) {
                const {
                    due_at,
                    goal,
                    quality_of_life_measure_id,
                    stake_match_amount
                } = failedAction.data;

                setCost(Number(stake_match_amount));
                setCostInput(true);
                setDate(due_at);
                setDateSuggestion(calculateDate(due_at));
                setDateInput(true);
                setDescription(goal);
                setDescriptionInput(true);
                setDisplayCostBtns(false);
                setDisplayDateBtns(false);
                setDisplayDescriptionBtns(false);
                setQolMeasure(quality_of_life_measure_id);
            }
        }, [addFailed, failedAction]);

        useEffect(() => {
            if (!history && holderID) {
                setIsFetchingHistory(true);

                fetchMemberActionStepHistorySuggestions(holderID, 1, HISTORY_PER_PAGE)
                    .then(async (response: Response) => {
                        const responseJson = await response.json();
                        const data = responseJson.goal_actions || [];
                        setHistory(data);
                        setHistoryPage(1);
                        // Check if there are more items (if we got the full per_page amount, there might be more)
                        setHistoryHasMore(data.length === HISTORY_PER_PAGE);
                        setIsFetchingHistory(false);
                    })
                    .catch((error: any) => {
                        console.error(
                            'Error fetching goal action history: ',
                            error
                        );
                        setIsFetchingHistory(false);
                    });
            }
        }, [history, holderID]);

        useImperativeHandle(ref, () => ({
            handleAddForProfile
        }));

        // Determine what to show based on current state
        // Show suggestions unless user has explicitly started manual input or is in edit mode
        const shouldShowInitialOptions = !showEdit && (!useTemplate && !useHistory && !useCustom);

        return (
            <>
                {!temporaryAction && (
                    <div className={classes.fadeContainer}>
                        {shouldShowInitialOptions ? (
                            <div className={classes.initialOptionsContainer}>
                                {/* <span className={classes.cardTitle}>
                                    Create Action Step
                                </span> */}
                                {/* 1. Write Your Own - Custom Action Step */}
                                <div className={classes.suggestionSection}>
                                    <SuggestionSectionHeader
                                        icon="fal fa-pencil"
                                        title="Custom"
                                        subtitle="Write your own action step"
                                        iconColor="#3C8FED"
                                    />
                                    <TemplateCard
                                        active={activeCustom}
                                        customDescription={customDescription}
                                        disabled={false}
                                        onClick={handleCustomActionClick}
                                        setCustomDescription={setCustomDescription}
                                        template={customTemplate}
                                    />
                                    {(hasTemplateActions || (history && history.length > 0)) && <SuggestionSectionFooter />}
                                </div>

                                {/* 2. Templates List */}
                                {hasTemplateActions && (
                                    <div className={classes.suggestionSection}>
                                        <SuggestionSectionHeader
                                            icon="fal fa-list"
                                            title="Templates"
                                            subtitle="Use action steps pre-designed with your team"
                                            iconColor="#F08F2F"
                                        />
                                        <ActionStepSuggestionList
                                            items={templateActions!}
                                            onItemClick={handleTemplateSelection}
                                            initialDisplayCount={2}
                                            enableOverlay={true}
                                        />
                                        {history && history.length > 0 && <SuggestionSectionFooter />}
                                    </div>
                                )}

                                {/* 3. History List */}
                                {isFetchingHistory && (
                                    <LoadingCircle />
                                )}
                                {history && history.length > 0 && (
                                    <div className={classes.suggestionSection}>
                                        <SuggestionSectionHeader
                                            icon="fal fa-history"
                                            title="History"
                                            subtitle="Re-create past completed action steps"
                                            iconColor="#0DB17B"
                                        />
                                        <ActionStepSuggestionList
                                            items={history.map(normalizeGoalActionToTemplate)}
                                            onItemClick={handleHistorySelection}
                                            onSeeMore={handleLoadMoreHistory}
                                            hasMore={historyHasMore}
                                            loading={isLoadingMoreHistory}
                                            initialDisplayCount={HISTORY_PER_PAGE}
                                            enableOverlay={true}
                                        />
                                        {!hasTemplateActions && <SuggestionSectionFooter />}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Fade in={true} timeout={500}>
                                <div className={classes.fadeContainer}>
                                    <Card
                                        className={classes.needWrapper}
                                        id="need-div"
                                        onClick={(e) => handleCardClick(e)}
                                        style={{ margin: '0px !important' }}
                                    >
                                        {overlay && (
                                            <Overlay text="Action Step Added!" />
                                        )}
                                        {!showEdit && (
                                            <span className={classes.cardTitle}>
                                                CREATE ACTION STEP
                                            </span>
                                        )}
                                        <ActionStepInput
                                            needType={`descriptions`}
                                            inputState={
                                                !showEdit
                                                    ? descriptionInput
                                                    : true
                                            }
                                            setInputState={setDescriptionInput}
                                            inputValState={description}
                                            setInputValState={setDescription}
                                            displayBtnState={
                                                showEdit
                                                    ? displayEditDescriptionBtns
                                                    : displayDescriptionBtns
                                            }
                                            setDisplayBtnState={
                                                showEdit
                                                    ? setDisplayEditDescriptionBtns
                                                    : setDisplayDescriptionBtns
                                            }
                                            showEdit={showEdit!}
                                            actionSuggestions={
                                                actionSuggestions
                                            }
                                            setGuide={setGuide!}
                                            intakeGoals={intakeGoals}
                                            isEditing={isEditing}
                                            setIsEditing={setIsEditing}
                                            setTemporaryAction={
                                                setTemporaryAction
                                            }
                                        />
                                        {description.length > 0 || showEdit ? (
                                            <ActionStepInput
                                                needType={`cost`}
                                                inputState={
                                                    !showEdit ? costInput : true
                                                }
                                                setInputState={setCostInput}
                                                inputValState={cost}
                                                setInputValState={setCost}
                                                displayBtnState={
                                                    showEdit
                                                        ? displayEditCostBtns
                                                        : displayCostBtns
                                                }
                                                setDisplayBtnState={
                                                    showEdit
                                                        ? setDisplayEditCostBtns
                                                        : setDisplayCostBtns
                                                }
                                                actionSuggestions={
                                                    actionSuggestions
                                                }
                                                setGuide={setGuide!}
                                                remainingBonusAmount={
                                                    remainingBonusAmount
                                                }
                                                bonusType={bonusType}
                                                isExtraBonusParticipant={isExtraBonusParticipant}
                                                samaritanYearlyDepleted={samaritanYearlyDepleted}
                                                yearlyFundsDepleted={yearlyFundsDepleted}
                                                showEdit={showEdit!}
                                                isEditing={isEditing}
                                                setIsEditing={setIsEditing}
                                                setTemporaryAction={
                                                    setTemporaryAction
                                                }
                                                adminBonusOverride={adminBonusOverride}
                                                setAdminBonusOverride={setAdminBonusOverride}
                                                isAdmin={isAdmin}
                                                from={from}
                                            />
                                        ) : null}
                                        {cost || cost === 0 || showEdit ? (
                                            <ActionStepInput
                                                needType={`due_dates`}
                                                inputState={
                                                    !showEdit ? dateInput : true
                                                }
                                                setInputState={setDateInput}
                                                inputValState={date}
                                                setInputValState={setDate}
                                                displayBtnState={
                                                    showEdit
                                                        ? displayEditDateBtns
                                                        : displayDateBtns
                                                }
                                                setDisplayBtnState={
                                                    showEdit
                                                        ? setDisplayEditDateBtns
                                                        : setDisplayDateBtns
                                                }
                                                actionSuggestions={
                                                    actionSuggestions
                                                }
                                                displayDateState={
                                                    dateSuggestion
                                                }
                                                setDisplayDateState={
                                                    setDateSuggestion
                                                }
                                                displayCustomDateInput={
                                                    displayCustomDateInput
                                                }
                                                setDisplayCustomDateInput={
                                                    setDisplayCustomDateInput
                                                }
                                                setGuide={setGuide!}
                                                saveAction={
                                                    from === 'intake'
                                                        ? handleAddForIntake
                                                        : () => {}
                                                }
                                                showEdit={showEdit!}
                                                isEditing={isEditing}
                                                setIsEditing={setIsEditing}
                                                setTemporaryAction={
                                                    setTemporaryAction
                                                }
                                            />
                                        ) : null}
                                        {showEdit ||
                                        (useTemplate &&
                                            cost &&
                                            date &&
                                            description &&
                                            from === 'intake') ? (
                                            <div
                                                className={
                                                    classes.submitBtnContainer
                                                }
                                            >
                                                {errorMessage && (
                                                    <span className={classes.errorText}>
                                                        <i className="fas fa-exclamation-triangle" />
                                                        {errorMessage}
                                                    </span>
                                                )}
                                                <DoneButton
                                                    handleClick={
                                                        handleAddForIntake
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                        {handleCancelClick && showCancel && (
                                            <div
                                                className={
                                                    classes.submitBtnContainer
                                                }
                                                style={{ paddingBottom: '0' }}
                                            >
                                                <Button
                                                    className={
                                                        classes.cancelFormButton
                                                    }
                                                    onClick={() =>
                                                        handleCancelClick()
                                                    }
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                    </Card>
                                </div>
                            </Fade>
                        )}
                    </div>
                )}
            </>
        );
    }
);

export default ActionStepForm;
